import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const IMG_MAIN =
  "https://customer-assets.emergentagent.com/job_shaji-nada-producer/artifacts/o63ojib8_4zoUPb_0-_1C_n7q6H7VIV5QSunUp6mCrqBy-0hx--dRZ0KDcwc_-8_GtQ0QXZUx3xImdZIbMcLJ915lS5UmjmYlf4aTDTMgdGB2hUEcOaE4z6yYDpWpkz1gACv-JSUcWEGBRQDpf2xOJaO5OuubLaStHZ4L1Ht3UmGbYV8vpIPurdshV2TTwDFhEYys_sR4.jpg";
const IMG_STREET =
  "https://customer-assets.emergentagent.com/job_shaji-nada-producer/artifacts/vfm7x6ks_gettyimages-633589058-2048x2048%20%281%29.jpeg";
const IMG_CROWD =
  "https://customer-assets.emergentagent.com/job_shaji-nada-producer/artifacts/wmlvipxr_wmremove-transformed%20%281%29.jpeg";
const IMG_PLAQUE =
  "https://customer-assets.emergentagent.com/job_shaji-nada-producer/artifacts/wu259sn8_gettyimages-880253904-2048x2048.jpeg";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

export default function CulturalContributions() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start end", "end start"],
  });

  // Subtle, layered parallax — each piece moves at a different rate.
  const yMain = useTransform(scrollYProgress, [0, 1], [40, -60]);
  const yStreet = useTransform(scrollYProgress, [0, 1], [80, -40]);
  const yCrowd = useTransform(scrollYProgress, [0, 1], [-30, 60]);
  const yPlaque = useTransform(scrollYProgress, [0, 1], [120, -20]);

  return (
    <section
      ref={wrapRef}
      data-testid="cultural-contributions"
      className="relative mt-32 md:mt-48 max-w-[1600px] mx-auto px-6 md:px-12"
    >
      {/* Ambient gold glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 right-0 w-[55vw] h-[55vw] max-w-[700px] max-h-[700px] rounded-full blur-[140px] opacity-[0.18]"
        style={{ background: "radial-gradient(circle, #CE1919 0%, transparent 60%)" }}
      />

      <div className="relative grid md:grid-cols-12 gap-12 md:gap-20 items-center">
        {/* TEXT — left */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="md:col-span-6 lg:col-span-6"
          data-testid="cultural-text"
        >
          <motion.p variants={reveal} custom={0} className="overline mb-6">
            Cultural Contributions
          </motion.p>

          <motion.h2
            variants={reveal}
            custom={1}
            className="font-serif-display text-4xl md:text-6xl lg:text-7xl text-white leading-[1.0]"
            data-testid="cultural-h2"
          >
            Honoring Global <br />
            <span className="italic text-[var(--accent)]">Artistic Legacy.</span>
          </motion.h2>

          <motion.div
            variants={reveal}
            custom={2}
            className="mt-10 w-16 h-px bg-[var(--accent)]"
            aria-hidden
          />

          <motion.p
            variants={reveal}
            custom={3}
            className="mt-10 text-white/75 font-light text-lg md:text-xl leading-relaxed max-w-xl"
          >
            Shaji Nada has been associated with several notable cultural and community initiatives,
            including the collaboration and support efforts behind the honorary street naming
            dedicated to legendary music composer{" "}
            <span className="text-white">A. R. Rahman</span> in Canada.
          </motion.p>

          <motion.p
            variants={reveal}
            custom={4}
            className="mt-6 text-white/65 font-light text-base md:text-lg leading-relaxed max-w-xl"
          >
            The recognition celebrated Rahman's global influence in music and his contribution to
            arts and culture worldwide. Through this initiative, Shaji Nada contributed to
            strengthening cultural connections and promoting the recognition of South Asian artistic
            excellence on an international platform.
          </motion.p>
        </motion.div>

        {/* LAYERED IMAGES — right */}
        <div className="md:col-span-6 lg:col-span-6 relative">
          <div className="relative aspect-[4/5] md:aspect-[5/6] w-full" data-testid="cultural-image-stack">
            {/* Main featured image */}
            <motion.figure
              style={{ y: yMain }}
              initial={{ opacity: 0, scale: 1.04 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="group absolute top-0 left-0 w-[78%] h-[78%] overflow-hidden rounded-md shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] border border-white/5 grain"
              data-testid="cultural-img-main"
            >
              <img
                src={IMG_MAIN}
                alt="A. R. Rahman with the Mayor of Markham unveiling the honorary 'A. R. Rahman' street sign in Canada"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
                style={{ filter: "brightness(0.92) contrast(1.05) saturate(0.95)" }}
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"
              />
            </motion.figure>

            {/* Top-right overlap — street naming */}
            <motion.figure
              style={{ y: yStreet }}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.0, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="group absolute top-[8%] right-0 w-[42%] aspect-[4/5] overflow-hidden rounded-md shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] border border-white/10"
              data-testid="cultural-img-street"
            >
              <img
                src={IMG_STREET}
                alt="A. R. Rahman with Toronto Mayor John Tory and Shaji Nada at an international cultural event"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
                style={{ filter: "brightness(0.88) contrast(1.05) saturate(0.95)" }}
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/50"
              />
            </motion.figure>

            {/* Bottom-left overlap — crowd / cultural celebration */}
            <motion.figure
              style={{ y: yCrowd }}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.0, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="group absolute bottom-[6%] left-[6%] w-[44%] aspect-[5/4] overflow-hidden rounded-md shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] border border-white/10"
              data-testid="cultural-img-crowd"
            >
              <img
                src={IMG_CROWD}
                alt="A. R. Rahman in conversation with Shaji Nada at a South Asian cultural celebration"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
                style={{ filter: "brightness(0.9) contrast(1.05) saturate(0.95)" }}
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"
              />
            </motion.figure>

            {/* Floating accent — plaque close-up */}
            <motion.figure
              style={{ y: yPlaque }}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.0, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="group absolute -bottom-[2%] right-[4%] w-[34%] aspect-square overflow-hidden rounded-md shadow-[0_20px_50px_-10px_rgba(200,169,107,0.25)] border border-[var(--accent)]/30"
              data-testid="cultural-img-plaque"
            >
              <img
                src={IMG_PLAQUE}
                alt="Civic recognition gathering in Markham, Canada honoring A. R. Rahman"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.08]"
                style={{ filter: "brightness(0.92) contrast(1.05) saturate(1.0)" }}
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-tr from-[#C8A96B]/15 via-transparent to-transparent"
              />
            </motion.figure>

            {/* Subtle gold corner accent */}
            <div
              aria-hidden
              className="absolute -top-3 -left-3 w-10 h-10 border-t border-l border-[var(--accent)]/60"
            />
            <div
              aria-hidden
              className="absolute -bottom-3 -right-3 w-10 h-10 border-b border-r border-[var(--accent)]/60"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
